import { useMemo } from "react";
import { useProviderSummariesByIdsQuery, useVendorListsQuery } from "./queries";
import { mapMyVendorListApiItemsToLists } from "../utils/myVendorListsMapper";
import {
  composeVendorLists,
  enrichAllListsFromMyVendors,
} from "../utils/myVendorListsComposition";
import useBookmarkedVendors from "../hooks/useBookmarkedVendors";

/**
 * Sponsor Vendor Lists — composes system lists (My Vendors, Bookmarked)
 * with user-created lists from GET /Sponsors/MyVendors/Lists.
 */
export function useMyVendorLists(
  sponsorId,
  { myVendors = [], bookmarkedProviderIds: bookmarkedProviderIdsProp } = {}
) {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useVendorListsQuery(sponsorId);

  const { bookmarkedProviderIds: bookmarkedFromHook } = useBookmarkedVendors(sponsorId);
  const bookmarkedProviderIds = bookmarkedProviderIdsProp ?? bookmarkedFromHook;

  const userLists = useMemo(
    () => mapMyVendorListApiItemsToLists(data?.items || []),
    [data?.items]
  );

  const myVendorsById = useMemo(
    () => new Map(myVendors.map((vendor) => [String(vendor.id).toLowerCase(), vendor])),
    [myVendors]
  );

  const bookmarkedIdsForLookup = useMemo(
    () => bookmarkedProviderIds.map(String).filter(Boolean),
    [bookmarkedProviderIds]
  );

  const { data: bookmarkedProviderSummaries = [] } =
    useProviderSummariesByIdsQuery(bookmarkedIdsForLookup);

  const providerDirectoryById = useMemo(
    () =>
      new Map(
        (bookmarkedProviderSummaries || [])
          .filter((vendor) => vendor?.id)
          .map((vendor) => [String(vendor.id).toLowerCase(), vendor])
      ),
    [bookmarkedProviderSummaries]
  );

  const lists = useMemo(() => {
    const composed = composeVendorLists({
      userLists,
      myVendors,
      bookmarkedProviderIds,
      myVendorsById,
      providerDirectoryById,
    });
    return enrichAllListsFromMyVendors(
      composed,
      myVendors,
      bookmarkedProviderSummaries
    );
  }, [
    userLists,
    myVendors,
    bookmarkedProviderIds,
    myVendorsById,
    providerDirectoryById,
    bookmarkedProviderSummaries,
  ]);

  const isEmpty = !isLoading && !isError && !!sponsorId && lists.length === 0;

  return {
    lists,
    userLists,
    isLoading,
    isError,
    isEmpty,
    error,
    refetch,
  };
}

export default useMyVendorLists;
